import {
  axiosInstance,
  getConfiguration,
} from '@foxtrotplatform/platform-micro-site-core';
import { AxiosError, AxiosRequestConfig } from 'axios';

import { DeveloperAppConfig } from '../config/developerAppConfig';
import {
  MutableSolutionAttributesDTO,
  RegisterSolutionRequestDTO,
  RegisterSolutionResponseDTO,
  SolutionCategory,
  SolutionDTO,
} from './models/solution';
import { ApiResponse } from './types';

export const getOrganizationSolutions = async (
  organizationId: string
): Promise<SolutionDTO[]> => {
  const config = (await getConfiguration()) as DeveloperAppConfig;
  const request: AxiosRequestConfig = {
    url: `${config.basePortalApiUrl}/solution`,
    params: {
      organizationId: organizationId,
    },
    method: 'get',
  };

  try {
    const { data }: ApiResponse<SolutionDTO[]> = await axiosInstance(request);

    return data;
  } catch (e: any) {
    // Allows for compatibility with Developer Portal API before fix is deployed through all envs.
    if ((e as AxiosError)?.response?.status === 404) {
      return Promise.resolve([]);
    }

    throw e;
  }
};

export const readCategoryOptions = async (): Promise<SolutionCategory[]> => {
  const categories: SolutionCategory[] = [
    {
      id: 'delivery-management',
      displayName: 'Delivery Management',
    },
    {
      id: 'transport-management',
      displayName: 'Transport Management',
    },
    {
      id: 'asset-management',
      displayName: 'Asset Management',
    },
    {
      id: 'freight',
      displayName: 'Freight',
    },
    {
      id: 'other',
      displayName: 'Other',
    },
  ];

  return Promise.resolve(categories);
};

export const registerSolution = async (
  organizationId: string,
  solution: RegisterSolutionRequestDTO
): Promise<RegisterSolutionResponseDTO> => {
  const config = (await getConfiguration()) as DeveloperAppConfig;
  const request: AxiosRequestConfig = {
    url: `${config.basePortalApiUrl}/solution/register`,
    params: {
      organizationId: organizationId,
    },
    data: solution,
    method: 'post',
  };

  const { data } = await axiosInstance<
    PortalApiResponse<RegisterSolutionResponseDTO>
  >(request);

  return data;
};

export const editSolution = async (
  organizationId: string,
  solutionId: string,
  solutionVersionId: string,
  solutionUpdate: MutableSolutionAttributesDTO
): Promise<unknown> => {
  const config = (await getConfiguration()) as DeveloperAppConfig;
  const request: AxiosRequestConfig = {
    url: `${config.basePortalApiUrl}/solution/${solutionId}/attributes`,
    params: {
      organizationId: organizationId,
      solutionVersionId: solutionVersionId,
    },
    data: solutionUpdate,
    method: 'patch',
  };

  const response = await axiosInstance<PortalApiResponse<unknown>>(request);

  return response;
};

export const fetchSolution = async (
  organizationId: string,
  solutionId: string
): Promise<SolutionDTO> => {
  const config = (await getConfiguration()) as DeveloperAppConfig;
  const request: AxiosRequestConfig = {
    url: `${config.basePortalApiUrl}/solution/${solutionId}`,
    params: {
      organizationId: organizationId,
    },
    method: 'get',
  };

  const { data } = await axiosInstance<PortalApiResponse<SolutionDTO>>(request);
  return data;
};
